/**
 * FORMATTER DOCUMENTAL CENTRAL — "NÃO INFORMADO".
 *
 * Regra global: campo PERTINENTE ao relatório e SEM informação é apresentado
 * como NÃO INFORMADO, igual no Preview e no PDF. Isto é representação
 * documental: o domínio continua com `null`, nada é gravado no banco e
 * nenhuma entidade técnica muda por causa de formatação.
 *
 * O que NÃO é ausência: `0` é valor, `false` é valor (vira SIM/NÃO). Por isso
 * é proibido o atalho `value || "NÃO INFORMADO"` — ele destruiria os dois.
 *
 * NÃO INFORMADO ≠ NÃO APLICÁVEL: o primeiro é "o campo se aplica mas não há
 * dado"; o segundo é decisão técnica e continua sendo um valor próprio
 * (`NOT_APPLICABLE`), preservado por este módulo.
 *
 * Campo deliberadamente REMOVIDO de um relatório não é caso deste módulo: ele
 * simplesmente não é emitido — não vira NÃO INFORMADO.
 */

export const NOT_INFORMED = "NÃO INFORMADO";
export const NOT_APPLICABLE = "NÃO APLICÁVEL";

/** Grandeza medida do domínio (`{ value, unit }`) e registros equivalentes (espessura mínima). */
interface MeasurementLike {
  value?: number | string | null;
  unit?: string | null;
}

export interface ReportValueOptions {
  /** Sufixo de unidade quando o valor vem "cru" (número/string) e a unidade está em outro campo. */
  unit?: string | null;
  /** Rótulos de boolean; o padrão é a representação documental SIM / NÃO. */
  booleanLabels?: { true: string; false: string };
  /** Casas decimais para números (padrão: o número como está, sem reformatar). */
  decimals?: number;
  /** Texto usado quando não há informação — só para seções com mensagem própria. */
  fallback?: string;
}

const isBlankString = (value: string): boolean => value.trim() === "";

/**
 * Ausência de informação: `null`, `undefined`, string vazia ou só espaços, e
 * números impossíveis de apresentar (`NaN`/`Infinity`, que nunca podem
 * aparecer no documento). `0` e `false` NÃO são ausência.
 */
export function isMissingValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return isBlankString(value);
  if (typeof value === "number") return !Number.isFinite(value);
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    const measurement = value as MeasurementLike;
    if ("value" in measurement) return isMissingValue(measurement.value);
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
}

function formatNumber(value: number, decimals?: number): string {
  if (decimals === undefined) return String(value);
  return value.toFixed(decimals);
}

function withUnit(text: string, unit: string | null | undefined): string {
  const suffix = typeof unit === "string" && !isBlankString(unit) ? ` ${unit.trim()}` : "";
  return `${text}${suffix}`;
}

/**
 * Valor pronto para o documento. Preserva valor válido (inclusive `0`),
 * converte boolean para SIM/NÃO, resolve `{ value, unit }` e devolve
 * NÃO INFORMADO para qualquer forma de ausência.
 */
export function formatReportValue(value: unknown, options: ReportValueOptions = {}): string {
  const fallback = options.fallback ?? NOT_INFORMED;
  if (isMissingValue(value)) return fallback;

  if (typeof value === "boolean") {
    const labels = options.booleanLabels ?? { true: "SIM", false: "NÃO" };
    return value ? labels.true : labels.false;
  }
  if (typeof value === "number") return withUnit(formatNumber(value, options.decimals), options.unit);
  if (typeof value === "string") {
    const text = value.trim();
    // NÃO APLICÁVEL já é um valor documental — nunca vira NÃO INFORMADO.
    return withUnit(text, options.unit);
  }
  if (typeof value === "object") {
    const measurement = value as MeasurementLike;
    if ("value" in measurement) {
      const inner = measurement.value;
      if (isMissingValue(inner)) return fallback;
      const unit = measurement.unit ?? options.unit ?? null;
      const rendered = typeof inner === "number" ? formatNumber(inner, options.decimals) : String(inner).trim();
      return withUnit(rendered, unit);
    }
  }
  return fallback;
}

/** Igual a `formatReportValue`, mas para campos que a regra técnica marcou como não aplicáveis. */
export function formatNotApplicable(): string {
  return NOT_APPLICABLE;
}
